// lib/licensing/tiers.ts
//
// Presets de módulos por tier on-prem. Usado tanto por el script de
// `scripts/issue-test-license.ts` (generar tokens de prueba) como por
// la UI de super admin `/super/licenses` (emitir/editar tokens desde la nube).
// Si cambiás los presets acá, se actualizan en ambos lugares.
//
// NOTA: son IDs de MÓDULO. Al emitir/editar se EXPANDEN a sus tablas
// (expandModulesWithTables en /super/licenses), porque el permiso se chequea por
// tabla (enabledModules.includes(table.tableName)). Los dominios
// operations/reports/ai/documents se prunean del build on-prem: incluir sus
// módulos en un tier no rompe (no hay ruta), pero solo tienen UI en SaaS/Desktop.

/** Baseline de sistema — presente en TODOS los tiers restringidos. */
export const SYSTEM_MODULES = [
  "companies",
  "company_subscriptions",
  "subscription_plans",
  "branches",
  "users",
  "user_invitations",
  "roles",
  "permissions",
  "profiles",
  "notifications",
  "audit_logs",
  "dashboard",
  "settings",
  "nomenclature",
  "control_panel",
  "deleted_records",
];

/** Comercial + stock básico (Starter). */
export const COMMERCIAL_MODULES = [
  "clients",
  "client_exemptions",
  "clients_exempt_documents",
  "sellers",
  "sales",
  "quotations",
  "products",
  "services",
  "brands",
  "categories",
  "suppliers",
  "stock",
  "stock_movements",
  "stock_status",
  "stock_configurations",
];

/** Fiscal SAR completo (Starter). */
export const FISCAL_MODULES = [
  "invoices",
  "invoices_configurations",
  "invoices_payments",
  "invoices_exemptions",
  "invoices_configuration_exempts",
  "invoices_exempts_documents",
  "invoices_cancelation_requests",
  "client_credits",
  "credit_notes",
  "credit_notes_configurations",
  "debit_notes",
  "debit_notes_configurations",
  "correlatives",
  "documents_types",
  "emission_points",
];

/** Inventario avanzado (Pro): bodegas, compras, traslados, conteos. */
export const INVENTORY_MODULES = [
  "warehouses",
  "purchase_orders",
  "stock_transfers",
  "inventory_counts",
  "stock_reservations",
];

/** Documentos/plantillas (Pro). Se prunea en on-prem (solo SaaS/Desktop). */
export const DOCUMENTS_MODULES = [
  "document_templates",
  "document_records",
  "attachments",
];

export type TierName = "starter" | "pro" | "all" | "custom";

export function resolveTier(name: TierName, customMods: string[]): string[] {
  switch (name) {
    case "starter":
      return [
        ...new Set([
          ...SYSTEM_MODULES,
          ...COMMERCIAL_MODULES,
          ...FISCAL_MODULES,
        ]),
      ];
    case "pro":
      return [
        ...new Set([
          ...SYSTEM_MODULES,
          ...COMMERCIAL_MODULES,
          ...FISCAL_MODULES,
          ...INVENTORY_MODULES,
          ...DOCUMENTS_MODULES,
        ]),
      ];
    case "all":
      return [];
    case "custom":
    default:
      return customMods;
  }
}

export function getMaxBranches(tier: TierName, override = 0): number {
  if (tier === "starter") return 1;
  if (tier === "pro") return 10;
  if (tier === "all") return 999_999;
  return override > 0 ? override : 999_999;
}

export const TIER_LABELS: Record<TierName, string> = {
  starter: "Starter (comercial + fiscal SAR · 1 sucursal)",
  pro: "Pro (Starter + inventario + documentos · 10 sucursales)",
  all: "All (todos los módulos · sin límite)",
  custom: "Custom (elegir a mano)",
};
